import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { HotelProposalItem } from '../../../types/proposalBundles';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import { getCircleNumber } from '@this/src/util/string/circledNumberMarks';

interface Props {
  item: HotelProposalItem;
  index: number;
  includeTax: boolean;
  stayDays: number | null;
  disabled: boolean;
  onSelect: (itemId: number) => void;
  onOpenDetail: (itemId: number) => void;
  isSpLayout?: boolean;
}

const HotelItemRow = ({
  item,
  index,
  includeTax,
  stayDays,
  disabled,
  onSelect,
  onOpenDetail,
  isSpLayout = false
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const itemNumber = index + 1;
  const circleNumber = getCircleNumber(index);
  const hotelName = item.title || '-';
  const rowStayDays = stayDays ?? item.hotel.stayDays ?? null;
  const nights = rowStayDays && rowStayDays > 0 ? rowStayDays : 1;
  const totalPrice = item.price !== null ? item.price * nights : null;
  const totalPriceWithTax = item.priceWithTax !== null ? item.priceWithTax * nights : null;
  const priceDisplay = formatPriceWithMark(includeTax ? totalPriceWithTax : totalPrice) ?? '-';
  const unitPriceDisplay = formatPriceWithMark(includeTax ? item.priceWithTax : item.price) ?? '-';
  const stayDisplay = rowStayDays && rowStayDays > 0 ? `${rowStayDays}泊${rowStayDays + 1}日` : '-';
  const breakfastDisplay =
    item.hotel.breakfastExist === null ? '記載なし' : item.hotel.breakfastExist ? '朝食あり' : '朝食なし';
  const smokeDisplay = item.hotel.smoke === null ? '記載なし' : item.hotel.smoke ? '喫煙' : '禁煙';
  const selectButtonStyle = {
    ...styles.selectButton,
    cursor: (item.selected || disabled ? 'not-allowed' : 'pointer') as 'pointer' | 'not-allowed',
    opacity: item.selected || disabled ? 0.5 : 1
  };

  const handleRowSelect = () => {
    if (!item.selected && !disabled) {
      onSelect(item.id);
    }
  };

  if (isSpLayout) {
    const rowStyle = {
      ...styles.spRow,
      backgroundColor: item.selected ? '#e6f4fb' : 'transparent'
    };
    return (
      <div style={rowStyle}>
        <div style={{ ...styles.numberCircle, alignSelf: 'flex-start' as const }}>{itemNumber}</div>
        <div style={styles.spLeft}>
          <span style={styles.spHotelName}>{hotelName}</span>
          <div style={styles.spPriceBlock}>
            <span style={styles.totalPriceText}>{priceDisplay}</span>
            <span style={styles.unitPriceText}>{unitPriceDisplay} /泊</span>
          </div>
        </div>
        <div style={styles.spRight}>
          <button type="button" style={styles.detailButton} onClick={() => onOpenDetail(item.id)}>
            詳細
          </button>
          <button
            type="button"
            style={selectButtonStyle}
            onClick={handleRowSelect}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </div>
    );
  }

  const rowStyle = {
    ...styles.row,
    backgroundColor: item.selected ? '#e6f4fb' : 'transparent'
  };

  return (
    <tr style={rowStyle}>
      <td style={styles.cellName}>
        <div style={styles.nameContainer}>
          <div style={styles.numberCircle}>{circleNumber}</div>
          <div>
            <span>{hotelName}</span>
          </div>
        </div>
      </td>
      <td style={styles.cellDetail}>
        <button type="button" style={styles.detailButton} onClick={() => onOpenDetail(item.id)}>
          詳細
        </button>
      </td>
      <td style={styles.cellAmenity}>
        {breakfastDisplay}／{smokeDisplay}
      </td>
      <td style={styles.cellStay}>{stayDisplay}</td>
      <td style={styles.cellPrice}>
        <div style={styles.priceContainer}>
          <div style={styles.priceTextBlock}>
            <span style={styles.totalPriceText}>{priceDisplay}</span>
            <span style={styles.unitPriceText}>{unitPriceDisplay} /泊</span>
          </div>
          <button
            type="button"
            style={selectButtonStyle}
            onClick={handleRowSelect}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

export default HotelItemRow;

const getStyles = (theme: AITTheme) => ({
  row: {
    borderBottom: '1px solid #ececec'
  },
  cellName: {
    padding: '12px 8px',
    fontSize: '13px',
    fontWeight: 500 as const,
    verticalAlign: 'middle' as const
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cellDetail: {
    padding: '12px 8px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellAmenity: {
    padding: '12px 8px',
    fontSize: '12px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellStay: {
    padding: '12px 8px',
    fontSize: '13px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  priceTextBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end' as const,
    gap: '2px'
  },
  totalPriceText: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  unitPriceText: {
    fontWeight: 400 as const,
    color: '#888888',
    fontSize: '11px'
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  detailButton: {
    backgroundColor: '#ffffff',
    color: theme.linkColor,
    border: `1px solid ${theme.linkColor}`,
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500 as const,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    whiteSpace: 'nowrap' as const
  },
  spRow: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: '12px',
    padding: '12px 4px',
    borderBottom: '1px solid #ececec'
  },
  spLeft: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  spHotelName: {
    fontSize: '13px',
    fontWeight: 'bold' as const
  },
  spPriceBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px'
  },
  spRight: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    alignItems: 'stretch' as const,
    flexShrink: 0
  }
});
